// src/pages/admin/printing/PrinterTest.jsx
import React, { useEffect, useMemo, useState } from "react";

/** يلتقط أسماء الطابعات من getPrinters() مهما اختلف شكل الحقول */
function extractPrinterNames(raw) {
  let arr = raw;
  if (typeof raw === "string") {
    try {
      arr = JSON.parse(raw);
    } catch {
      arr = [raw];
    }
  }
  if (arr && Array.isArray(arr.printers)) arr = arr.printers;
  if (!Array.isArray(arr)) arr = [];

  const rows = arr.map((p) => {
    if (typeof p === "string") {
      return { Name: p, DriverName: "", PortName: "", Status: "" };
    }
    return {
      Name: p?.Name ?? p?.name ?? "",
      DriverName: p?.DriverName ?? p?.driverName ?? "",
      PortName: p?.PortName ?? p?.portName ?? "",
      Status: p?.PrinterStatus ?? p?.status ?? "",
    };
  });

  const names = rows.map((r) => String(r.Name || "").trim()).filter(Boolean);
  return { rows, names: Array.from(new Set(names)) };
}

export default function PrinterTest() {
  const [out, setOut] = useState("");
  const [rows, setRows] = useState([]);
  const [names, setNames] = useState([]);

  const [p1, setP1] = useState(localStorage.getItem("printer_1") || "");
  const [p2, setP2] = useState(localStorage.getItem("printer_2") || "");
  const [p3, setP3] = useState(localStorage.getItem("printer_3") || "");
  const [p4, setP4] = useState(localStorage.getItem("printer_4") || "");
  const [p5, setP5] = useState(localStorage.getItem("printer_5") || "");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    doList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doList() {
    setLoading(true);
    setOut("");
    try {
      const api = await import("tauri-plugin-printer-v2");
      const raw = await api.getPrinters();
      const { rows: r, names: n } = extractPrinterNames(raw);
      setRows(r);
      setNames(n);

      // اختيارات افتراضية لأول 5 أسماء
      const sel = [p1, p2, p3, p4, p5];
      for (let i = 0; i < Math.min(5, n.length); i++) {
        if (!sel[i]) {
          if (i === 0) setP1(n[i]);
          if (i === 1) setP2(n[i]);
          if (i === 2) setP3(n[i]);
          if (i === 3) setP4(n[i]);
          if (i === 4) setP5(n[i]);
        }
      }

      setOut("تم جلب الطابعات.");
    } catch (e) {
      setOut("LIST ERROR: " + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  function saveSelection() {
    localStorage.setItem("printer_1", p1 || "");
    localStorage.setItem("printer_2", p2 || "");
    localStorage.setItem("printer_3", p3 || "");
    localStorage.setItem("printer_4", p4 || "");
    localStorage.setItem("printer_5", p5 || "");
    localStorage.setItem("print_method", "plugin");
    setOut(
      `تم الحفظ:\n` +
        `1 = ${p1 || "-"}\n2 = ${p2 || "-"}\n3 = ${p3 || "-"}\n4 = ${
          p4 || "-"
        }\n5 = ${p5 || "-"}`
    );
  }

  function chooseAs(which, name) {
    if (which === 1) setP1(name);
    if (which === 2) setP2(name);
    if (which === 3) setP3(name);
    if (which === 4) setP4(name);
    if (which === 5) setP5(name);
  }

  async function doTest() {
    try {
      const api = await import("tauri-plugin-printer-v2");
      const { printHtml } = api;

      const mkHtml = (
        slot,
        name
      ) => `<!doctype html><html><body style="width:80mm;padding:8px;font-family:Tahoma">
        <h3 style="margin:0 0 6px">TEST PRINT</h3>
        <div>Slot: ${slot}</div>
        <div>Printer: ${name || "-"}</div>
        <div style="margin-top:6px">${new Date().toLocaleString("ar-IQ")}</div>
      </body></html>`;

      const wkArgs = [
        "--disable-smart-shrinking",
        "--margin-left",
        "0",
        "--margin-right",
        "0",
        "--margin-top",
        "0",
        "--margin-bottom",
        "0",
        "--dpi",
        "203",
      ];
      const print_settings = JSON.stringify({ wkhtml_args: wkArgs });

      const job = (id, printer, slot) =>
        printer
          ? printHtml({
              id,
              html: mkHtml(slot, printer),
              printer,
              print_settings,
              margin: { top: 0, right: 0, bottom: 0, left: 0, unit: "mm" },
              page_width: 80,
              page_height: 180,
              orientation: "portrait",
              copies: 1,
              quality: 100,
              grayscale: false,
              remove_after_print: false,
            })
          : Promise.resolve();

      await Promise.all([
        job(`test-${Date.now()}-1`, p1, 1),
        job(`test-${Date.now()}-2`, p2, 2),
        job(`test-${Date.now()}-3`, p3, 3),
        job(`test-${Date.now()}-4`, p4, 4),
        job(`test-${Date.now()}-5`, p5, 5),
      ]);

      setOut(
        `تم إرسال اختبار إلى:\n- ${p1 || "(غير محددة)"}\n- ${
          p2 || "(غير محددة)"
        }\n- ${p3 || "(غير محددة)"}\n- ${p4 || "(غير محددة)"}\n- ${
          p5 || "(غير محددة)"
        }`
      );
    } catch (e) {
      setOut("PRINT ERROR: " + (e?.message || e));
    }
  }

  const canSave = useMemo(
    () => !!(p1 || p2 || p3 || p4 || p5),
    [p1, p2, p3, p4, p5]
  );

  const Picker = ({ label, value, onChange }) => (
    <div className="flex flex-col">
      <label className="text-sm text-gray-600 mb-1">{label}</label>
      <select
        className="border rounded px-2 py-1 min-w-[260px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— اختر —</option>
        {names.map((n) => (
          <option key={`${label}-${n}`} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="p-4 space-y-4" dir="rtl">
      <h2 className="text-lg font-bold">إعداد الطابعات (حتى ٥)</h2>

      <div className="flex flex-wrap items-end gap-3">
        <Picker label="الطابعة 1 (فاتورة)" value={p1} onChange={setP1} />
        <Picker label="الطابعة 2 (تجهيز)" value={p2} onChange={setP2} />
        <Picker label="الطابعة 3" value={p3} onChange={setP3} />
        <Picker label="الطابعة 4" value={p4} onChange={setP4} />
        <Picker label="الطابعة 5" value={p5} onChange={setP5} />

        <button
          onClick={saveSelection}
          disabled={!canSave}
          className={`px-4 py-2 rounded text-white ${
            canSave
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          حفظ الاختيارات
        </button>

        <button
          onClick={doTest}
          className="px-4 py-2 rounded bg-amber-400 hover:bg-amber-500"
        >
          طباعة اختبار إلى الجميع
        </button>

        <button
          onClick={doList}
          disabled={loading}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          title="تحديث قائمة الطابعات"
        >
          {loading ? "جارٍ التحديث…" : "تحديث القائمة"}
        </button>
      </div>

      {/* جدول الطابعات مع مفاتيح مفيدة + أزرار تعيين سريعة */}
      <div className="overflow-auto border rounded">
        <table className="w-full text-right text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2">الاسم</th>
              <th className="p-2">التعريف</th>
              <th className="p-2">المنفذ</th>
              <th className="p-2">الحالة</th>
              <th className="p-2">تعيين</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="p-2">{r.Name || "-"}</td>
                  <td className="p-2">{r.DriverName || "-"}</td>
                  <td className="p-2">{r.PortName || "-"}</td>
                  <td className="p-2">{String(r.Status ?? "")}</td>
                  <td className="p-2">
                    <div className="flex gap-2 flex-wrap">
                      {[1, 2, 3, 4, 5].map((slot) => (
                        <button
                          key={`as-${slot}`}
                          onClick={() => chooseAs(slot, r.Name)}
                          className="px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 border"
                          title={`تعيين كطابعة ${slot}`}
                        >
                          اختر كـ {slot}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="p-3 text-gray-500 text-center" colSpan={5}>
                  لا توجد بيانات — اضغط "تحديث القائمة".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <pre className="bg-black text-green-300 p-3 rounded overflow-auto max-h-[50vh] text-xs whitespace-pre-wrap">
        {out}
      </pre>
    </div>
  );
}
